const stripNegativeSign = (value: string): string => value.replace(/-\s?/, '')

// Currencies the platform actually supports end-to-end. MUST stay in sync with
// tripening-core's CurrencyCodes enum — anything not here is rejected by the API currency
// middleware (a 400). Used to gate honoring a `?currency=` URL param as the session currency.
export const SUPPORTED_CURRENCY_CODES = [
  'USD', 'EUR', 'CNY', 'JPY', 'CAD', 'AUD', 'GBP', 'ETB', 'IDR', 'PHP', 'SGD',
]

export const isSupportedCurrency = (code: unknown): code is string =>
  typeof code === 'string' && SUPPORTED_CURRENCY_CODES.includes(code.toUpperCase())

// Display metadata for supported currencies. Shared by the account store and the header selector
// so the symbol (notably the disambiguated dollar family) is defined in exactly one place.
export const SUPPORTED_CURRENCIES: Array<{ code: string; name: string; symbol: string; flag?: string }> = [
  // "US$", not a bare "$". Google's verifier resolves a bare "$" by PAGE CONTEXT, not by any
  // canonical currency: on zh-locale landings for SG/HK properties it read our USD amounts as
  // SGD/HKD and FX-converted them (2026-08-12 accuracy misses were exactly cached/1.28 — e.g.
  // 102.66 = 131.41/1.28 on hotel 4721236). USD gets the same disambiguation as the rest of the
  // dollar family; no dollar-family currency may render a bare "$" anywhere.
  { code: 'USD', name: 'U.S. Dollar', symbol: 'US$', flag: 'fi fi-us' },
  { code: 'EUR', name: 'Euro', symbol: '€', flag: 'fi fi-eu' },
  // "CN¥", not a bare "¥" — that glyph is also JPY's, and this table feeds the raw
  // `{{currency.symbol}}{{amount}}` interpolation in the booking block's "Price from" teaser,
  // which bypasses formatWithIntl. A bare "¥" there is the same ambiguity that made Google read
  // HKD prices as USD; keep every entry here distinct from every other.
  { code: 'CNY', name: 'Chinese Yuan', symbol: 'CN¥', flag: 'fi fi-cn' },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥', flag: 'fi fi-jp' },
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'CA$', flag: 'fi fi-ca' },
  { code: 'AUD', name: 'Australian Dollar', symbol: 'A$', flag: 'fi fi-au' },
  { code: 'GBP', name: 'British Pound', symbol: '£', flag: 'fi fi-gb' },
  { code: 'ETB', name: 'Ethiopian Birr', symbol: 'Br', flag: 'fi fi-et' },
  { code: 'IDR', name: 'Indonesian Rupiah', symbol: 'Rp', flag: 'fi fi-id' },
  { code: 'PHP', name: 'Philippine Peso', symbol: '₱', flag: 'fi fi-ph' },
  { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$', flag: 'fi fi-sg' },
]

// Build a {code, name, symbol} record for any code, falling back to Intl for name/symbol.
export const currencyInfo = (code: string): { code: string; name: string; symbol: string } => {
  const known = SUPPORTED_CURRENCIES.find((c) => c.code === code)
  if (known) return { code: known.code, name: known.name, symbol: known.symbol }
  let name = code
  try {
    name = new Intl.DisplayNames(['en'], { type: 'currency' }).of(code) || code
  } catch {
    // keep code as name
  }
  return { code, name, symbol: currencySymbol(code) }
}

// Always format on the client from amount + currency so every price on the page renders with the
// SAME symbol, regardless of what the backend put in `.formatted`. Several currencies share the bare
// "$" glyph (USD, AUD, CAD, SGD...); mixing a backend "$809.79" with a client-derived "SGD 269.93"
// is exactly what produced the confused Google SGD booking box. Mirrors tripening-core's
// CURRENCY_SYMBOL_OVERRIDES so the symbol matches what the backend emits elsewhere (emails, feeds).
// Only for currencies whose Intl 'symbol' rendering we deliberately disagree with — everything else
// must go through Intl 'symbol' (see formatWithIntl), which already disambiguates HK$/MX$/NT$/CN¥.
const CURRENCY_SYMBOL_OVERRIDES: Record<string, string> = {
  // USD included: Intl en-US renders USD as a bare "$", which Google's verifier resolves by page
  // context — on zh-locale SG/HK landings it read our USD prices as SGD/HKD (2026-08-12 misses
  // were exactly cached/1.28). "US$" is Intl's own rendering for USD outside en-US locales.
  USD: 'US$',
  AUD: 'A$',
  CAD: 'CA$',
  SGD: 'S$',
}

export const currencySymbol = (currency: string): string => {
  const code = currency || 'USD'
  const override = CURRENCY_SYMBOL_OVERRIDES[code]
  if (override) return override
  try {
    const parts = new Intl.NumberFormat('en-US', { style: 'currency', currency: code }).formatToParts(0)
    return parts.find((p) => p.type === 'currency')?.value || code
  } catch {
    return code
  }
}

export const formatWithIntl = (amount: number, currency: string): string => {
  const safeCurrency = currency || 'USD'

  try {
    // 'symbol', NOT 'narrowSymbol'. narrowSymbol collapses every dollar-family currency to a bare
    // "$" (HKD, MXN, TWD, NZD, CLP, COP, ARS ...) and CNY to a bare "¥" (same glyph as JPY). Google
    // Hotel Ads sends its verifier to this page in whatever currency it advertised; a bare "$901.41"
    // on an HKD landing is read as USD, so Google compares USD 901.41 against a feed price of
    // HKD 901.41 and records a ~7.8x price-accuracy miss. ~31% of GHA landing crawls arrive in one
    // of these ambiguous currencies. 'symbol' renders HK$/MX$/NT$/CN¥ and an ISO prefix otherwise,
    // which is unambiguous; the overrides below then restore the few symbols we prefer over Intl's.
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: safeCurrency,
      currencyDisplay: 'symbol',
    }).format(amount)
    const override = CURRENCY_SYMBOL_OVERRIDES[safeCurrency]
    // Force our own symbol where we disagree with Intl (e.g. Intl renders SGD as "SGD 269.93").
    // Preserve a leading minus sign (e.g. discounts) while swapping the symbol.
    if (override) {
      return formatted.replace(/^(-)?[^\d]*/, (_m, neg) => (neg || '') + override)
    }
    return formatted
  } catch {
    return `${currencySymbol(safeCurrency)}${amount.toFixed(2)}`
  }
}

export const extractNumericPrice = (rawPrice: unknown): number | null => {
  if (rawPrice === undefined || rawPrice === null) {
    return null
  }

  if (typeof rawPrice === 'number') {
    return Number.isFinite(rawPrice) ? rawPrice : null
  }

  if (typeof rawPrice === 'string') {
    const parsed = parseFloat(rawPrice)
    return Number.isFinite(parsed) ? parsed : null
  }

  if (typeof rawPrice === 'object') {
    const amount = (rawPrice as Record<string, unknown>)?.amount
    return extractNumericPrice(amount)
  }

  return null
}

export const normalizePriceValue = (rawPrice: unknown): number | null => {
  const extracted = extractNumericPrice(rawPrice)
  return extracted === null ? null : Math.abs(extracted)
}

export const formatPriceDisplay = (price: any, fallbackCurrency = 'USD'): string => {
  if (!price) {
    return ''
  }

  const value = price.converted ?? price

  const amountCandidates = [
    value?.amount,
    price?.amount,
    value?.converted?.amount,
    price,
  ]

  let signedAmount: number | null = null
  for (const candidate of amountCandidates) {
    signedAmount = extractNumericPrice(candidate)
    if (signedAmount !== null) {
      break
    }
  }

  const currency =
    value?.currency ??
    value?.converted?.currency ??
    price?.currency ??
    fallbackCurrency

  // Intentionally re-format from amount + currency instead of trusting the backend `.formatted`
  // string, so the symbol is consistent everywhere (the backend renders SGD/AUD/CAD with a bare "$").
  if (signedAmount !== null) {
    return formatWithIntl(Math.abs(signedAmount), currency)
  }

  // Last resort only: no numeric amount available at all.
  const formatted = typeof value?.formatted === 'string' ? value.formatted : null
  if (formatted) {
    return stripNegativeSign(formatted)
  }

  return ''
}

// Convenience for templates: format a price object (with optional `.converted`) consistently.
// Pass a fallback currency so amount-only objects still render with the active currency's symbol.
export const formatPrice = (price: any, fallbackCurrency = 'USD'): string =>
  formatPriceDisplay(price, fallbackCurrency)

// Google's price-accuracy crawler reads the total off the page as Microdata, and since the
// 2026 generic-crawler rules the value it reads has to be the *visible* number: a hidden
// <meta itemprop="price"> or a content="..." override is explicitly rejected. So the one number
// a guest sees has to be split into the part that carries `priceCurrency` (the symbol) and the
// part that carries `price` (the numerals), without changing a character of what is rendered.
// Everything here is formatted with Intl en-US (see formatWithIntl), so the numeral run is always
// "1,234.56" — group separator ",", decimal ".".
export type FormattedPriceParts = { prefix: string; number: string; suffix: string }

export const splitFormattedPrice = (formatted: unknown): FormattedPriceParts => {
  const text = typeof formatted === 'string' ? formatted : String(formatted ?? '')
  // Lazy prefix, then the longest run of digits and separators, then whatever trails it —
  // "US$1,234.56" splits as "US$" / "1,234.56" / "", "1 234,56 €" as "" / "1 234,56" / " €".
  const match = text.match(/^(.*?)(\d[\d.,\u00A0\u202F' ]*\d|\d)(.*)$/s)
  if (!match) return { prefix: '', number: text, suffix: '' }
  return { prefix: match[1] ?? '', number: match[2] ?? text, suffix: match[3] ?? '' }
}
